import {cpServicePost} from 'api';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';
import {sendSentryException} from 'utils/error-handling';
import {
    type VerbatimReviewsResults,
    type VerbatimReviewsResponse,
    type VerbatimReviewsAPIResponse,
} from 'api/facilities/verbatim-reviews.types';
import {type Filters} from 'pages/facilities/driver-feedback/verbatim-reviews/VerbatimReviews.types';
import {type ApiResponse} from 'api/api.types';
import dayjs from 'utils/dayjs';

/**
 * Formats the response from the API.
 */
const formatResponse = (
    response: ApiResponse<VerbatimReviewsResponse>
): VerbatimReviewsResults => {
    const camelizedResponse = camelizeKeys(
        response
    ) as VerbatimReviewsAPIResponse;
    const {data: innerData} = camelizedResponse;

    const formattedResponse: VerbatimReviewsResults = {
        data: {
            ...innerData?.data,
            lastProcessed: innerData?.data?.lastProcessed
                ? dayjs(innerData.data.lastProcessed).format(
                      'dddd MMMM D, hh:mm A [CT].'
                  )
                : null,
        },
        meta: {
            limit: innerData?.meta?.limit || 25,
            offset: innerData?.meta?.offset || 0,
            nextOffset: innerData?.meta?.nextOffset || null,
            totalCount: innerData?.meta?.totalCount || 0,
            filteredCount: innerData?.meta?.filteredCount || 0,
        },
    };

    return formattedResponse;
};

/**
 * Formats the API request
 */
const formatRequest = (data: Filters) => {
    const transformed: Filters = {
        ...data,
        startDate: dayjs(data.startDate).format('YYYY-MM-DD'),
        endDate: dayjs(data.endDate).format('YYYY-MM-DD'),
    };

    const request = convertKeysToSnakeCase(transformed);

    return request;
};

/**
 * Gets a facility's verbatim reviews
 */
const postOperatorReviewsSearch = async (
    facilityId: number,
    filters: Filters,
    config = {}
): Promise<VerbatimReviewsResults> => {
    try {
        const formattedRequest = formatRequest(filters);
        const response = await cpServicePost<VerbatimReviewsResponse>(
            `facilities/${facilityId}/operator-reviews/search`,
            {...formattedRequest},
            config
        );
        const formattedResponse = formatResponse(response);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);
        throw error;
    }
};

export default postOperatorReviewsSearch;
